import { useEffect } from "react";
import type { RefObject } from "react";

interface SelArg {
  value: string;
  start: number;
  end: number;
  delim: string;
}

export function wrapSelection({ value, start, end, delim }: SelArg) {
  if (start === end) {
    const v = value.slice(0, start) + delim + delim + value.slice(start);
    return { value: v, start: start + delim.length, end: start + delim.length };
  }
  const v =
    value.slice(0, start) + delim + value.slice(start, end) + delim + value.slice(end);
  return { value: v, start: start + delim.length, end: end + delim.length };
}

export function unwrapSelection({ value, start, end, delim }: SelArg) {
  const d = delim.length;
  if (start - d < 0 || end + d > value.length) return null;
  if (value.slice(start - d, start) !== delim || value.slice(end, end + d) !== delim) {
    return null;
  }
  const v = value.slice(0, start - d) + value.slice(start, end) + value.slice(end + d);
  return { value: v, start: start - d, end: end - d };
}

interface PrefixArg {
  value: string;
  start: number;
  end: number;
  prefix: string;
}

export function prefixLines({ value, start, end, prefix }: PrefixArg) {
  const lineStart = value.lastIndexOf("\n", start - 1) + 1;
  const lineEnd = value.indexOf("\n", end);
  const block = value.slice(lineStart, lineEnd === -1 ? undefined : lineEnd);
  const prefixed = block
    .split("\n")
    .map((l) => prefix + l)
    .join("\n");
  return {
    value: value.slice(0, lineStart) + prefixed + (lineEnd === -1 ? "" : value.slice(lineEnd)),
  };
}

type ChangeFn = (next: string) => void;
type Ref = RefObject<HTMLTextAreaElement | null>;

export function useMarkdownShortcuts(ref: Ref, value: string, onChange: ChangeFn) {
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const handler = (e: KeyboardEvent) => {
      const mod = e.metaKey || e.ctrlKey;
      if (!mod) return;
      const { selectionStart: s, selectionEnd: en } = el;
      const apply = (next: { value: string; start: number; end: number }) => {
        e.preventDefault();
        onChange(next.value);
        requestAnimationFrame(() => {
          el.setSelectionRange(next.start, next.end);
        });
      };
      const wrapOrToggle = (delim: string) => {
        const off = unwrapSelection({ value, start: s, end: en, delim });
        if (off) return apply(off);
        apply(wrapSelection({ value, start: s, end: en, delim }));
      };
      switch (e.key.toLowerCase()) {
        case "b":
          if (!e.shiftKey) return wrapOrToggle("*");
          break;
        case "i":
          if (!e.shiftKey) return wrapOrToggle("_");
          break;
        case "e":
          if (!e.shiftKey) return wrapOrToggle("`");
          break;
        case "x":
          if (e.shiftKey) return wrapOrToggle("~");
          break;
        case "k": {
          if (e.shiftKey) return;
          e.preventDefault();
          const sel = value.slice(s, en);
          if (sel) {
            const inserted = `[${sel}](url)`;
            const next = value.slice(0, s) + inserted + value.slice(en);
            onChange(next);
            requestAnimationFrame(() => {
              const urlStart = s + sel.length + 3;
              el.setSelectionRange(urlStart, urlStart + 3);
            });
          } else {
            const inserted = `[](url)`;
            const next = value.slice(0, s) + inserted + value.slice(s);
            onChange(next);
            requestAnimationFrame(() => el.setSelectionRange(s + 1, s + 1));
          }
          return;
        }
        case "c":
          if (e.shiftKey) {
            e.preventDefault();
            const sel = value.slice(s, en);
            const block = "```\n" + sel + "\n```";
            const next = value.slice(0, s) + block + value.slice(en);
            onChange(next);
            requestAnimationFrame(() => {
              const caret = s + 4 + sel.length;
              el.setSelectionRange(caret, caret);
            });
          }
          return;
        case ".":
          if (e.shiftKey) {
            const next = prefixLines({ value, start: s, end: en, prefix: "> " });
            e.preventDefault();
            onChange(next.value);
          }
          return;
        case "7":
          if (e.shiftKey) {
            const next = prefixLines({ value, start: s, end: en, prefix: "1. " });
            e.preventDefault();
            onChange(next.value);
          }
          return;
        case "8":
          if (e.shiftKey) {
            const next = prefixLines({ value, start: s, end: en, prefix: "- " });
            e.preventDefault();
            onChange(next.value);
          }
          return;
      }
    };
    el.addEventListener("keydown", handler);
    return () => el.removeEventListener("keydown", handler);
  }, [ref, value, onChange]);
}
